import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import type {
  AgreementStatus,
  OrganizationResponse,
  UpdateOrganizationRequest
} from '@/api/generated-organizations'
import { initApiService } from '@/services/init'
import { getGlobalT } from '@/i18n'
import { extractApiErrorMessage, logUnexpectedFailure } from '@/utils/errorHandling'
import { forgetRememberedFinancingRegimes, rememberFinancingRegimes } from '@/utils/rememberedFinancingRegimes'

/**
 * Organization Store
 *
 * Manages the current user's organization data and provides actions
 * for fetching and updating organization information.
 */
export const useOrganizationStore = defineStore('organization', () => {
  const t = getGlobalT()

  // State
  const organization = ref<OrganizationResponse | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Computed
  const hasOrganization = computed(() => organization.value !== null)

  // Whatever the server last answered is what the next cold load shows until it answers again.
  watch(organization, (loaded) => {
    if (loaded) rememberFinancingRegimes(loaded.id, loaded.enabledFinancingRegimes ?? [])
  })

  /**
   * Fetch current user's organization
   */
  async function fetchOrganization() {
    loading.value = true
    error.value = null

    try {
      const api = initApiService()
      const response = await api.organizations.getCurrentOrganization()
      organization.value = response.data
    } catch (err: unknown) {
      error.value = extractApiErrorMessage(err, t('errors.loadOrganization'))
      logUnexpectedFailure('Failed to fetch organization:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch the organization only when it is not loaded yet. Safe to call
   * repeatedly; no-ops once the organization is in the store.
   */
  async function ensureLoaded() {
    if (!organization.value) {
      await fetchOrganization()
    }
  }

  /**
   * Update current user's organization
   */
  async function updateOrganization(updates: UpdateOrganizationRequest) {
    loading.value = true
    error.value = null

    try {
      const api = initApiService()
      const response = await api.organizations.updateCurrentOrganization(updates)
      organization.value = response.data
      return response.data
    } catch (err: unknown) {
      error.value = extractApiErrorMessage(err, t('errors.updateOrganization'))
      logUnexpectedFailure('Failed to update organization:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Upload organization logo
   */
  async function uploadLogo(file: File) {
    loading.value = true
    error.value = null

    try {
      const api = initApiService()
      const response = await api.organizations.uploadOrganizationLogo(file)
      // Update organization to reflect new logo URL
      if (organization.value) {
        organization.value = {
          ...organization.value,
          logoUrl: response.data.logoUrl
        }
      }
      return response.data
    } catch (err: unknown) {
      error.value = extractApiErrorMessage(err, t('errors.uploadLogo'))
      logUnexpectedFailure('Failed to upload logo:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Delete organization logo
   */
  async function deleteLogo() {
    loading.value = true
    error.value = null

    try {
      const api = initApiService()
      await api.organizations.deleteOrganizationLogo()
      // Update organization to reflect logo removal
      if (organization.value) {
        organization.value = {
          ...organization.value,
          logoUrl: undefined
        }
      }
    } catch (err: unknown) {
      error.value = extractApiErrorMessage(err, t('errors.deleteLogo'))
      logUnexpectedFailure('Failed to delete logo:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Replace what the organization is bound by with what accepting the outstanding
   * agreement versions answered (#4494), so nothing reads a stale "outstanding".
   */
  function setAgreements(agreements: AgreementStatus[]) {
    if (organization.value) {
      organization.value = { ...organization.value, agreements }
    }
  }

  /**
   * Clear organization data (on logout)
   */
  function clearOrganization() {
    forgetRememberedFinancingRegimes()
    organization.value = null
    error.value = null
    loading.value = false
  }

  return {
    // State
    organization,
    loading,
    error,

    // Computed
    hasOrganization,

    // Actions
    fetchOrganization,
    ensureLoaded,
    updateOrganization,
    uploadLogo,
    deleteLogo,
    setAgreements,
    clearOrganization
  }
})
